import { FormInstance } from "antd";
import dayjs, { Dayjs } from "dayjs";
import moment, { Moment } from "moment";
import { dateFormat, dbDateFormat } from "../constants/constants";

export const formatDate = (
  date?: string | Date,
  format: string = dateFormat,
) => {
  return moment(date || new Date()).format(format);
};

export const formatTime = (date?: string | Date, format: string = "h:mm A") => {
  return moment(date || new Date()).format(format);
};

export const formatOnlyTime = (
  time: string,
  defaultFormat: string = "h:mm:ss",
  format: string = "h:mm A",
) => {
  return moment(time, defaultFormat).format(format);
};

export const convertMinutesToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}:${remainingMinutes.toString().padStart(2, "0")}`;
};

export function convertMinutesToHoursWithText(minutes: number): string {
  const duration = moment.duration(minutes, "minutes");
  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();

  return !minutes
    ? "0"
    : `${
        hours
          ? `${hours} hr${hours !== 1 ? "s" : ""} ${mins ? "and" : ""} `
          : ""
      }${mins ? `${mins} min` : ""} `;
}

// calculate just minutes
export const calculateMinutes = (startTime: string, endTime: string) => {
  const start = moment(startTime, "h:mm A");
  const end = moment(endTime, "h:mm A");

  return end.diff(start, "minutes");
};

export const getTimeDurationInDays = (
  startDate: string,
  endDate: string,
): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const diffInMilliseconds = end.getTime() - start.getTime();
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

  return Math.round(diffInDays);
};

export const formatMinutesToHours = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const formatSecondToMinutes = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

export const isExpired = (expirationDate: string, format = dbDateFormat) => {
  const currentDate = moment();
  const date = moment(expirationDate).format(format);
  return moment(date, format).isBefore(currentDate);
};

export const disableTodayAndFutureDates = (current: any) => {
  return current && current >= moment().startOf("day");
};

export const disableFutureDates = (current: any) => {
  return current && current > moment().endOf("day");
};

export const disablePastDates = (current: any) => {
  return current && current < moment().startOf("day");
};

export const getLastTenYears = (): string[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) =>
    (currentYear - index).toString(),
  );
};

export const convertMinutesToTime = (minutes: number = 0) => {
  const duration = moment.duration(minutes, "minutes");

  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();

  return `${hours}:${mins.toString().padStart(2, "0")}`;
};

export const formatUTCTime = (date: string | Moment) => {
  return moment.utc(date).local().toISOString();
};
export const formatUTCDate = (
  date: string | Moment,
  format = "DD MMM, YYYY",
) => {
  return moment.utc(date).format(format);
};

export const UTCTimeFormat = (date: string | Moment, format = "hh:mm A") => {
  return moment.utc(date).format(format);
};

export const getStartAndEndDateInAMonth = (
  date: Date,
  format = dbDateFormat,
) => {
  return {
    startDate: moment(date).startOf("month").format(format),
    endDate: moment(date).endOf("month").format(format),
  };
};

export function getMonthDayDiff(dateString: string): string {
  if (!dateString) return "--";
  const start = moment(dateString);
  const now = moment();

  const months = now.diff(start, "months");
  const days = now.diff(start.clone().add(months, "months"), "days");

  const monthLabel = `${months} Month${months !== 1 ? "s" : ""}`;
  const dayLabel = `${days} Day${days !== 1 ? "s" : ""}`;

  return `${monthLabel}, ${dayLabel}`;
}

export function formatShortRelativeTime(date: string | Date): string {
  const now = moment();
  const input = moment(date);
  const diffSeconds = now.diff(input, "seconds");

  if (diffSeconds < 60) return "now";

  const diffMinutes = now.diff(input, "minutes");
  if (diffMinutes < 60) return `${diffMinutes} m ago`;

  const diffHours = now.diff(input, "hours");
  if (diffHours < 24) return `${diffHours} h ago`;

  const diffDays = now.diff(input, "days");
  if (diffDays < 30) return `${diffDays} d ago`;

  const diffMonths = now.diff(input, "months");
  if (diffMonths < 12) return `${diffMonths} mo ago`;

  const diffYears = now.diff(input, "years");
  return `${diffYears} y ago`;
}

export const disablePastTimes = (date: Dayjs | null) => {
  if (!date) return {};

  const now = dayjs();
  if (!date.isSame(now, "day")) return {};

  const hours = Array.from({ length: now.hour() }, (_, i) => i);
  const minutes = Array.from({ length: now.minute() }, (_, i) => i);

  return {
    disabledHours: () => hours,
    disabledMinutes: (selectedHour: number) =>
      selectedHour === now.hour() ? minutes : [],
  };
};

/**
 * Disable past times for "from_time" picker.
 * Works only if selected from_date is today.
 */
export const disableFromTimes =
  (form: FormInstance) => (date: Dayjs | null) => {
    const fromDate = form.getFieldValue("from_date");
    const now = dayjs();

    // If from_date is not selected or not today, allow all times
    if (!fromDate || !dayjs(fromDate).isSame(now, "day")) {
      return {};
    }

    // Disable hours/minutes before the current time
    const hours = Array.from({ length: now.hour() }, (_, i) => i);
    const minutes = Array.from({ length: now.minute() }, (_, i) => i);

    return {
      disabledHours: () => hours,
      disabledMinutes: (selectedHour: number) =>
        selectedHour === now.hour() ? minutes : [],
    };
  };

/**
 * Disable earlier times for "to_time" picker.
 * If from_date and to_date are the same, disables times earlier than from_time.
 */
export const disableToTimes = (form: FormInstance) => (date: Dayjs | null) => {
  const fromDate = form.getFieldValue("from_date");
  const toDate = form.getFieldValue("to_date");
  const fromTime = form.getFieldValue("shift_start_time");

  if (!date || !fromTime) return {};

  // If dates are different, allow all times
  if (!fromDate || !toDate || !dayjs(fromDate).isSame(toDate, "day")) {
    return {};
  }

  const startHour = dayjs(fromTime).hour();
  const startMinute = dayjs(fromTime).minute();

  return {
    disabledHours: () => Array.from({ length: startHour }, (_, i) => i),
    disabledMinutes: (selectedHour: number) =>
      selectedHour === startHour
        ? Array.from({ length: startMinute }, (_, i) => i)
        : [],
  };
};

/**
 * Disable all to_date options earlier than from_date.
 */
export const disableToDates = (form: FormInstance) => (current: Dayjs) => {
  const fromDate = form.getFieldValue("from_date");
  if (!fromDate) return current && current < dayjs().startOf("day");
  return current && current < dayjs(fromDate).startOf("day");
};

export const parseTime = (timeStr?: string) => {
  if (!timeStr) return null;

  // Try multiple formats for flexibility (with and without seconds)
  const parsed = dayjs(timeStr, "HH:mm:ss", true).isValid()
    ? dayjs(timeStr, "HH:mm:ss")
    : dayjs(timeStr, "HH:mm", true).isValid()
    ? dayjs(timeStr, "HH:mm")
    : null;

  return parsed ? parsed.format("hh:mm A") : "-";
};
